import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import Layout from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { DEPARTMENTS } from "@/lib/departments";
import { compressImage } from "@/lib/image";
import { Heart } from "@phosphor-icons/react";

export default function DoudouEdit() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/doudous/${id}`);
        setForm({
          name: data.name || "",
          description: data.description || "",
          department: data.department || "",
          photo: data.photo || null,
          secret_detail: data.secret_detail || "",
        });
      } catch { toast.error("Doudou introuvable"); navigate("/dashboard"); }
    })();
  }, [id, navigate]);

  const handlePhoto = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 15 * 1024 * 1024) { toast.error("Photo trop lourde (max 15 Mo)"); return; }
    try {
      const compressed = await compressImage(file);
      setForm({ ...form, photo: compressed });
    } catch { toast.error("Impossible de lire cette photo"); }
  };

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.patch(`/doudous/${id}`, form);
      toast.success("Fiche mise à jour !");
      navigate(`/doudou/${id}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur");
    } finally { setSaving(false); }
  };

  if (!form) return <Layout><div className="max-w-2xl mx-auto px-4 py-20 text-stone-500">Chargement…</div></Layout>;

  return (
    <Layout>
      <div className="max-w-2xl mx-auto px-4 py-12">
        <h1 className="font-display text-3xl font-black text-stone-800" data-testid="edit-doudou-title">Modifier la fiche</h1>
        <p className="text-stone-600 mt-2">Mettez à jour les informations de ce doudou.</p>

        <form onSubmit={save} className="mt-8 bg-white rounded-3xl border border-rose-100 p-8 space-y-5 shadow-sm">
          <div>
            <Label>Nom du doudou *</Label>
            <Input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="rounded-2xl mt-1 py-6" data-testid="edit-doudou-name-input" />
          </div>

          <div>
            <Label>Photo</Label>
            <label className="mt-2 flex flex-col items-center justify-center w-full min-h-[12rem] rounded-2xl border-2 border-dashed border-rose-200 bg-rose-50/50 cursor-pointer hover:bg-rose-50 transition overflow-hidden" data-testid="edit-doudou-photo-upload">
              {form.photo ? (
                <img src={form.photo} alt="aperçu" className="w-full max-h-[28rem] object-contain rounded-2xl" />
              ) : (
                <div className="flex flex-col items-center text-stone-500">
                  <Heart size={42} weight="duotone" className="text-rose-300" />
                  <span className="text-sm mt-2">Cliquez pour changer la photo</span>
                </div>
              )}
              <input type="file" accept="image/*" className="hidden" onChange={handlePhoto} data-testid="edit-doudou-photo-input" />
            </label>
          </div>

          <div>
            <Label>Description</Label>
            <Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="Couleur, taille, particularités..." className="rounded-2xl mt-1 min-h-[100px]" data-testid="edit-doudou-description-input" />
          </div>

          <div>
            <Label>Département</Label>
            <select required value={form.department} onChange={(e) => setForm({ ...form, department: e.target.value })} className="w-full rounded-2xl mt-1 py-3 px-3 border border-stone-200 bg-white text-stone-700" data-testid="edit-doudou-department-select">
              <option value="">Choisir un département</option>
              {DEPARTMENTS.map((d) => (<option key={d.code} value={d.code}>{d.code} — {d.name}</option>))}
            </select>
          </div>

          <div>
            <Label>Détail secret <span className="text-xs text-stone-400 font-normal">(connu de vous uniquement)</span></Label>
            <Input value={form.secret_detail} onChange={(e) => setForm({ ...form, secret_detail: e.target.value })} className="rounded-2xl mt-1 py-6" placeholder="Tache de café, marque cousue à l'intérieur..." data-testid="edit-doudou-secret-input" />
            <p className="text-xs text-stone-500 mt-1.5">Ce détail nous sert à confirmer votre identité en cas de réclamation litigieuse.</p>
          </div>

          <div className="flex gap-2 pt-2">
            <Button type="button" variant="ghost" onClick={() => navigate(`/doudou/${id}`)} className="rounded-full" data-testid="edit-doudou-cancel">
              Annuler
            </Button>
            <Button type="submit" disabled={saving} className="bg-orange-500 hover:bg-orange-600 rounded-full py-6 font-bold flex-1" data-testid="edit-doudou-save">
              {saving ? "Enregistrement..." : "Enregistrer les modifications"}
            </Button>
          </div>
        </form>
      </div>
    </Layout>
  );
}
